/* eslint-disable @typescript-eslint/no-unused-vars */
import {useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useSlugResolution} from '../context/SlugResolutionContext';
import {useRoomSlugResolution} from '../context/RoomSlugResolutionContext';
import {PLAYER_PRES_ID_QUERY, PLAYER_ROOM_ID_QUERY, PRESENTER_CODE_QUERY} from '../incase-domain/online';
import {omitRedirectParams} from '../util/redirect';

interface PresentationParams {
    presentationId: string | null;
    roomId: string | null;
    presenterCode: string | null;
    params: {[key: string]: string};
}

export const usePresentationParams = (): PresentationParams => {
    const [searchParams] = useSearchParams();
    const slugResolution = useSlugResolution();
    const roomSlugResolution = useRoomSlugResolution();

    const presentationParams = useMemo(() => {
        if (slugResolution) {
            const paramsObject = Object.fromEntries(searchParams.entries());
            const roomId = paramsObject[PLAYER_ROOM_ID_QUERY] || null;
            const presenterCode = paramsObject[PRESENTER_CODE_QUERY] || null;

            const {[PLAYER_ROOM_ID_QUERY]: _, [PRESENTER_CODE_QUERY]: __, ...extraParams} = paramsObject;

            return {
                presentationId: slugResolution.presentationId,
                roomId,
                presenterCode,
                params: omitRedirectParams({...slugResolution.params, ...extraParams}),
            };
        }

        if (roomSlugResolution) {
            const paramsObject = Object.fromEntries(searchParams.entries());

            const {[PRESENTER_CODE_QUERY]: _, ...extraParams} = paramsObject;

            const slugParams: {[key: string]: string} = {};
            roomSlugResolution.params.forEach(({key, value}) => {
                slugParams[key] = value;
            });

            return {
                presentationId: null,
                roomId: roomSlugResolution.roomId,
                presenterCode: paramsObject[PRESENTER_CODE_QUERY] || roomSlugResolution.presenterCode || null,
                params: omitRedirectParams({...slugParams, ...extraParams}),
            };
        }

        const paramsObject = Object.fromEntries(searchParams.entries());
        const presentationId = paramsObject[PLAYER_PRES_ID_QUERY] || null;
        const roomId = paramsObject[PLAYER_ROOM_ID_QUERY] || null;
        const presenterCode = paramsObject[PRESENTER_CODE_QUERY] || null;

        const {[PLAYER_PRES_ID_QUERY]: _, [PLAYER_ROOM_ID_QUERY]: __, [PRESENTER_CODE_QUERY]: ____, ...params} = paramsObject;

        return {presentationId, roomId, presenterCode, params: omitRedirectParams(params)};
    }, [searchParams, slugResolution, roomSlugResolution]);

    return presentationParams;
};
